import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { GoogleIcon } from "@/pages/Login";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Signup() {
    const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
    const [loading, setLoading] = useState(false);
    const { refresh } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/register", form);
            await refresh();
            toast.success("Workspace created", { description: "Welcome to ClientSpace." });
            navigate("/dashboard");
        } catch (err) {
            toast.error("Could not create account", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFA]">
            <div className="hidden lg:flex flex-col justify-between p-10 bg-[#0F172A] text-white relative overflow-hidden">
                <Logo />
                <div className="max-w-md relative z-10">
                    <p className="overline text-slate-400 mb-4">A better client experience</p>
                    <h2 className="font-display text-3xl leading-snug tracking-tight">
                        Set up your workspace in under a minute. Your first portal is one click away.
                    </h2>
                </div>
                <div className="text-xs text-slate-500 relative z-10">© 2026 ClientSpace</div>
                <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#C55A46]/15 blur-3xl pointer-events-none" />
            </div>

            <div className="flex items-center justify-center p-6 lg:p-10">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-8"><Logo /></div>
                    <h1 className="font-display text-3xl tracking-tight text-slate-900">Create your workspace</h1>
                    <p className="text-slate-500 mt-2">Free forever for your first 3 clients.</p>

                    <button
                        onClick={startGoogleAuth}
                        data-testid="google-signup-btn"
                        className="mt-8 w-full flex items-center justify-center gap-3 py-3 rounded-lg border border-slate-200 bg-white text-slate-700 font-medium hover:border-slate-300 transition-colors"
                    >
                        <GoogleIcon /> Continue with Google
                    </button>

                    <div className="flex items-center gap-3 my-6">
                        <div className="flex-1 h-px bg-slate-200" />
                        <span className="text-xs text-slate-400">or with email</span>
                        <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-slate-700">Your name</Label>
                                <Input id="name" required value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="signup-name" className="ring-terra" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="company" className="text-slate-700">Company</Label>
                                <Input id="company" required value={form.company_name}
                                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                                    data-testid="signup-company" className="ring-terra" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-slate-700">Email</Label>
                            <Input id="email" type="email" required value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                data-testid="signup-email" className="ring-terra" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-slate-700">Password</Label>
                            <Input id="password" type="password" required minLength={6} value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                data-testid="signup-password" className="ring-terra" />
                        </div>
                        <button type="submit" disabled={loading} data-testid="signup-submit"
                            className="btn-terra w-full py-3 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70">
                            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create workspace
                        </button>
                    </form>

                    <p className="text-sm text-slate-500 mt-6 text-center">
                        Already have an account? <Link to="/login" className="text-[#C55A46] font-medium hover:underline" data-testid="link-login">Sign in</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
